"use client";

import {
  Suspense,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSessionRouteStore } from "@/app/session/SessionRouteStoreProvider";
import AppThemeMenu from "@/components/layout/AppThemeMenu";
import { MediaSearchBar } from "@/features/session/components/MediaSearchBar";
import UserAccountMenu from "@/components/layout/UserAccountMenu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LogOut,
  Search,
  X,
} from "@/components/ui/icons";
import { useMediaAuth } from "@/hooks/useMediaAuth";
import { buildPostSurveyPath, normalizeSessionId } from "@/lib/participantSessions";
import { cn } from "@/lib/utils";
import { useStore } from "@/store";

const userFacingHeaderRootClass =
  "theme-header-user-surface relative z-20 box-border w-full max-w-full overflow-x-clip overflow-y-visible";
const headerAmbientClass = "theme-header-ambient absolute inset-0 pointer-events-none";
const headerSafeAreaClass = "pointer-events-none box-border h-[env(safe-area-inset-top)]";
const headerInnerBaseClass =
  "relative flex w-full max-w-full flex-wrap items-center gap-3 box-border px-4 py-3 sm:flex-nowrap sm:px-6 sm:py-0";
const headerActionsBaseClass = "ml-auto flex items-center gap-3";
const roundControlBaseClass =
  "inline-flex size-9 items-center justify-center rounded-full border transition-[background-color,border-color,color,box-shadow] duration-150 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20";
const sessionControlClass =
  "theme-control-surface border text-muted-foreground hover:text-foreground";

function readSessionIdFromPathname(pathname: string | null) {
  if (typeof pathname !== "string") return null;

  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "session" || parts.length < 2) {
    return null;
  }

  const candidate = parts[1];
  if (!candidate) return null;

  try {
    return normalizeSessionId(decodeURIComponent(candidate));
  } catch {
    return normalizeSessionId(candidate);
  }
}

type UserFacingHeaderMode = "participant" | "session";
type SessionHeaderControls = {
  onOpenSessionLog: () => void;
  onViewAllSessionData: () => void;
  onExportAllSessionData: () => void;
  sessionLogAlertCount: number;
};

function HeaderBrandWordmark({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 items-center gap-[0.65rem]", className)}>
      <Image
        src="/branding/imags-round-logo.png"
        alt=""
        width={1500}
        height={1500}
        priority
        className="theme-dark-aero-logo size-9 shrink-0 object-contain sm:size-12"
      />
      <div className="min-w-0 font-[family:var(--font-brand-display),var(--font-app-sans),ui-sans-serif,sans-serif] text-[clamp(1.35rem,0.9rem+1vw,2rem)] font-bold uppercase leading-none tracking-[0.12em] text-foreground sm:text-[clamp(1.9rem,1.1rem+1.2vw,2.8rem)]">
        IMAGS
      </div>
    </div>
  );
}

function UserFacingHeaderFrame({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <header className={userFacingHeaderRootClass}>
      <div className={headerAmbientClass} aria-hidden="true" />
      <div
        className="theme-header-session-accent absolute inset-0 pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="theme-header-session-glow absolute inset-x-12 bottom-0 h-[1.9rem] pointer-events-none"
        aria-hidden="true"
      />
      <div className={headerSafeAreaClass} aria-hidden="true" />
      <div
        className={cn(
          headerInnerBaseClass,
          "w-full max-w-full bg-[color:var(--surface-card)] sm:min-h-16 sm:px-[clamp(1rem,1.6vw,1.9rem)]",
        )}
      >
        {children}
      </div>
    </header>
  );
}

function HeaderRoundControlButton({
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(roundControlBaseClass, sessionControlClass, className)}
      {...props}
    />
  );
}

function UserFacingHeader({
  mode,
  sessionHeaderControls,
}: {
  mode: UserFacingHeaderMode;
  sessionHeaderControls?: SessionHeaderControls;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, logout } = useMediaAuth();
  const storeSessionId = useStore.use.sessionId();
  const setBlockedSessionId = useStore.use.setBlockedSessionId();
  const sessionActive = useStore.use.sessionActive();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const isSessionPage = mode === "session";
  const routeSessionId =
    normalizeSessionId(searchParams.get("sessionId")) ??
    readSessionIdFromPathname(pathname) ??
    normalizeSessionId(storeSessionId);
  const shouldForcePostSurvey =
    Boolean(routeSessionId) &&
    (sessionActive || pathname?.startsWith("/session/") || pathname === "/postsurvey");
  const showMediaSearch =
    isSessionPage &&
    (user?.provider === "spotify" ||
      user?.provider === "youtube" ||
      user?.provider === "applemusic");
  const showParticipantLogoutButton = !isSessionPage && Boolean(user);

  const handleLogout = async () => {
    if (shouldForcePostSurvey && routeSessionId) {
      setBlockedSessionId(routeSessionId);
      router.replace(buildPostSurveyPath(routeSessionId, { logout: true }));
      return;
    }

    await logout();
    router.replace("/");
  };

  const handleOpenSessionLog = () => {
    if (!isSessionPage) return;
    sessionHeaderControls?.onOpenSessionLog();
  };

  const handleViewAllSessionData = () => {
    if (!isSessionPage) return;
    sessionHeaderControls?.onViewAllSessionData();
  };

  const handleExportAllSessionData = () => {
    if (!isSessionPage) return;
    sessionHeaderControls?.onExportAllSessionData();
  };

  return (
    <UserFacingHeaderFrame>
      <HeaderBrandWordmark />

      {showMediaSearch ? (
        <div className="hidden min-w-0 flex-1 justify-center md:flex">
          <MediaSearchBar onCloseMenus={() => setMobileSearchOpen(false)} />
        </div>
      ) : null}

      <div className={cn(headerActionsBaseClass, "gap-[0.45rem]")}>
        <AppThemeMenu />

        {showParticipantLogoutButton ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void handleLogout();
            }}
            className="min-h-9 rounded-full px-3.5"
          >
            <LogOut className="size-4" />
            Logout
          </Button>
        ) : null}

        {showMediaSearch ? (
          <HeaderRoundControlButton
            onClick={() => setMobileSearchOpen(true)}
            className="md:hidden"
            aria-label="Open search"
            title="Search"
          >
            <Search className="size-4" />
          </HeaderRoundControlButton>
        ) : null}

        {isSessionPage && user ? (
          <UserAccountMenu
            user={user}
            variant="session"
            onLogout={handleLogout}
            onExportAllData={
              isSessionPage ? handleExportAllSessionData : undefined
            }
            onOpenSessionLog={
              isSessionPage ? handleOpenSessionLog : undefined
            }
            sessionLogAlertCount={
              isSessionPage
                ? sessionHeaderControls?.sessionLogAlertCount
                : undefined
            }
            onViewAllData={
              isSessionPage ? handleViewAllSessionData : undefined
            }
          />
        ) : null}
      </div>

      {showMediaSearch ? (
        <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
          <DialogContent
            unstyled
            showCloseButton={false}
            className="theme-menu-surface fixed left-1/2 top-[calc(env(safe-area-inset-top)+0.5rem)] z-50 flex max-h-[calc(100dvh-1rem)] min-h-0 w-[calc(100vw-1rem)] max-w-sm -translate-x-1/2 flex-col overflow-hidden rounded-[1.25rem] border sm:top-[4.25rem] sm:max-w-[32rem] sm:max-h-[calc(100dvh-5.25rem)]"
          >
            <div className="flex items-center justify-between gap-3 border-b border-border/60 px-3 py-2.5">
              <DialogHeader className="min-w-0 text-left">
                <DialogTitle className="text-sm font-semibold text-foreground">
                  Search
                </DialogTitle>
              </DialogHeader>
              <HeaderRoundControlButton
                onClick={() => setMobileSearchOpen(false)}
                className="size-8"
                aria-label="Close search"
              >
                <X className="size-4" />
              </HeaderRoundControlButton>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              <MediaSearchBar
                autoFocus
                inlineResults
                compact
                onCloseMenus={() => setMobileSearchOpen(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </UserFacingHeaderFrame>
  );
}

function UserFacingHeaderFallback() {
  return (
    <UserFacingHeaderFrame>
      <HeaderBrandWordmark />
      <div className={cn(headerActionsBaseClass, "gap-[0.45rem]")}>
        <AppThemeMenu />
      </div>
    </UserFacingHeaderFrame>
  );
}

function SessionRouteHeader() {
  const setSessionLogOverlayOpen = useSessionRouteStore(
    (state) => state.setSessionLogOverlayOpen
  );
  const sessionLogAlertCount = useSessionRouteStore(
    (state) => state.sessionLogAlertCount
  );
  const setWorkspaceDrawerPanel = useSessionRouteStore(
    (state) => state.setWorkspaceDrawerPanel
  );
  const requestExportAllUserData = useSessionRouteStore(
    (state) => state.requestExportAllUserData
  );

  return (
    <UserFacingHeader
      mode="session"
      sessionHeaderControls={{
        onOpenSessionLog: () => setSessionLogOverlayOpen(true),
        onViewAllSessionData: () => setWorkspaceDrawerPanel("user"),
        onExportAllSessionData: () => requestExportAllUserData(),
        sessionLogAlertCount,
      }}
    />
  );
}

export function ParticipantHeader() {
  return (
    <Suspense fallback={<UserFacingHeaderFallback />}>
      <UserFacingHeader mode="participant" />
    </Suspense>
  );
}

export function SessionHeader() {
  return (
    <Suspense fallback={<UserFacingHeaderFallback />}>
      <SessionRouteHeader />
    </Suspense>
  );
}
